import React, { useCallback, useEffect, useRef, useState } from 'react';

import dayjs, { extend, unix } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { motion } from 'framer-motion';

import { FeatureFlagManager } from '@pkg/components/Sections/Admin/FeatureFlags';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { copyToClipboard } from '@pkg/utils';

import config from '@app/config';
import { intervalCheck, setEnableIntervalCheck, stopInterval } from '@app/store/actions/admin-info';
import { type AdminInfo as AdminInfoType } from '@app/store/types/admin-info';
import { useNavigationStore } from '@app/store/zustand';
import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

import './AdminInfo.scss';

extend(duration);
extend(relativeTime);
function AdminInfoUi() {
    const [minimize, setMinimize] = useState(!!parseInt(localStorage.getItem('admin-minimize') || '0'));
    const adminInfo = useAppSelector((state) => state.adminInfo);
    const accessToken = useAuthTokens((state) => state.token.access_token);
    const userEmail = useAppSelector((state) => state.user.userAccount?.user?.email);

    const [adminInformation, setAdminInformation] = useState<AdminInfoType | null>(null);
    const [showFeatureFlags, setShowFeatureFlags] = useState(false);
    const [enableCheck, setEnableCheck] = useState(false);
    const [showAll, setShowAll] = useState(true);
    const [stopped, setStopped] = useState(false);
    const [copied, setCopied] = useState(false);

    const dispatch = useAppDispatch();

    const handleMinimize = useCallback(() => {
        const isMinimized = !minimize;
        setMinimize(isMinimized);
        localStorage.setItem('admin-minimize', isMinimized ? '1' : '0');
    }, [minimize]);

    const copyCurl = useCallback(() => {
        copyToClipboard(
            `curl -s -H 'Content-Type: application/json' -H 'Authorization: Bearer ${accessToken}' ${config.apiUrl}`,
        );
    }, [accessToken]);

    useEffect(() => {
        if (adminInfo) {
            setAdminInformation(adminInfo.information);
            setEnableCheck(adminInfo.enableIntervalCheck);
        }
    }, [adminInfo]);

    const infoMarkup = (
        <>
            <div className="admin-info__item">
                <div className="admin-info__item-title">Role:</div>
                <div className="admin-info__item-data">{adminInformation?.role}</div>
            </div>
            <div className="admin-info__item">
                <div className="admin-info__item-title">Host:</div>
                <div className="admin-info__item-data">{adminInformation?.host}</div>
            </div>
            <div className="admin-info__item">
                <div className="admin-info__item-title">User:</div>
                <div className="admin-info__item-data">{userEmail}</div>
            </div>
            <div className="admin-info__item">
                <div className="admin-info__item-title">Api:</div>
                <div className="admin-info__item-data">{adminInformation?.apiVersion}</div>
            </div>
            <div className="admin-info__item">
                <div className="admin-info__item-title">Web:</div>
                <div className="admin-info__item-data">{adminInformation?.webVersion}</div>
            </div>
            {showAll && (
                <>
                    <div className="admin-info__item">
                        <div className="admin-info__item-title">Zoom:</div>
                        <div className="admin-info__item-data">{adminInformation?.zoom}</div>
                    </div>
                    <div className="admin-info__item">
                        <div className="admin-info__item-title">Screen:</div>
                        <div className="admin-info__item-data">{adminInformation?.screenSize}</div>
                    </div>
                    <div className="admin-info__item">
                        <div className="admin-info__item-title">View:</div>
                        <div className="admin-info__item-data">{adminInformation?.viewSize}</div>
                    </div>
                    <div className="admin-info__item">
                        <div className="admin-info__item-title">Access Expiry:</div>
                        <div className="admin-info__item-data">
                            {dayjs(unix(adminInformation?.accessTokenExpiresAt || 0)).fromNow()}
                        </div>
                    </div>
                    <div className="admin-info__item">
                        <div className="admin-info__item-title">Refresh Expiry:</div>
                        <div className="admin-info__item-data">
                            {dayjs(unix(adminInformation?.refreshTokenExpiresAt || 0)).fromNow()}
                        </div>
                    </div>
                    <div className="admin-info__item">
                        <div className="admin-info__item-title">2FA:</div>
                        <div className="admin-info__item-data">
                            {adminInformation?.twoFactorAuthTypes?.join(' | ') || 'N/A'}
                        </div>
                    </div>
                    <div className="admin-info__item">
                        <div className="admin-info__item-title">2FA Expiry:</div>
                        <div className="admin-info__item-data">
                            {adminInformation?.twoFactorAuthTypes
                                ? dayjs(unix(adminInformation?.twoFactorExpiresAt || 0)).fromNow()
                                : 'N/A'}
                        </div>
                    </div>
                </>
            )}
        </>
    );

    const bodyRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        bodyRef.current = document.getElementById('root') as HTMLDivElement;
    }, []);

    return (
        <motion.div
            drag
            dragConstraints={bodyRef.current ? bodyRef : undefined}
            dragMomentum={false}
            className={`admin-info ${minimize ? 'minimize' : ''}  ${showAll ? '' : 'show-less'}`}
        >
            <div className={'admin-info__buttons'}>
                <button
                    className={' min-button'}
                    onClick={() => {
                        handleMinimize();
                    }}
                    title={minimize ? 'Maximize' : 'Minimize'}
                >
                    {minimize ? '➕️' : '➖️'}
                </button>
                {minimize ? null : (
                    <>
                        <button
                            className={`show-all-button`}
                            onClick={() => {
                                setShowAll(!showAll);
                            }}
                            title={showAll ? 'Less Info' : 'More Info'}
                        >
                            {showAll ? '🔤' : '🔠'}
                        </button>
                        <button
                            onClick={() => {
                                setCopied(true);
                                setTimeout(() => {
                                    setCopied(false);
                                }, 750);
                                copyCurl();
                            }}
                            title={'Copy curl link'}
                        >
                            {copied ? '✅️' : '🔗'}
                        </button>
                        <button
                            onClick={() => {
                                if (stopped) {
                                    dispatch(intervalCheck());
                                    setStopped(false);
                                } else {
                                    stopInterval();
                                    setStopped(true);
                                }
                            }}
                        >
                            {stopped ? '▶️' : '⏹'}
                        </button>
                        <div className="admin-info__buttons-info">{` W:${adminInformation?.webVersion} A:${adminInformation?.apiVersion}`}</div>
                    </>
                )}
            </div>
            {minimize ? null : (
                <>
                    {infoMarkup}
                    <hr />
                    <div className="admin-info__buttons-holder">
                        <button
                            style={{ color: showFeatureFlags ? 'white' : 'limegreen' }}
                            className="admin-info__clickable"
                            onClick={() => {
                                setShowFeatureFlags(!showFeatureFlags);
                            }}
                        >
                            {!showFeatureFlags ? 'Show' : 'Hide'} Feature Flags
                        </button>
                        <hr />

                        <button
                            style={{ color: enableCheck ? 'limegreen' : 'white' }}
                            className="admin-info__clickable"
                            onClick={() => {
                                dispatch(setEnableIntervalCheck(enableCheck));
                            }}
                        >
                            {enableCheck ? 'Interval Check Enabled' : 'Enable Interval check '}
                        </button>
                    </div>
                    <hr />
                    {showFeatureFlags && <FeatureFlagManager />}
                </>
            )}
        </motion.div>
    );
}

export function AdminInfo() {
    const isAdmin = useAppSelector((state) => state.user?.userClass?.isAdmin);
    const showAdminInfo = useAppSelector((state) => state.featureFlags.features.showAdminInfo);
    const { hideAdminOptions } = useNavigationStore((state) => state);

    return (isAdmin || showAdminInfo) && !hideAdminOptions ? <AdminInfoUi /> : null;
}
